import { ENTITY_LABELS } from "../../../../utils/labelMapping";
import { TableCell, TableHead, TableRow, Tooltip } from "@mui/material";
import Table from "../../../../Component/UI/Table/Table";
import { StyledTableCell, StyledTableRow } from "../../../../Component/UI/Table/Table";
import classes from "./CategoryDims.module.css";
import { extractDomain } from "../../../../utils/helpers";
import { getMetricExplainations } from "../../../../utils/constant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

function PaginatedCategoryDimensionsTable({
 data,
 uniqueBrands,
 brandDomains,
 styleClasses,
 formatName,
 isChecked,
 maxBrandsPerTable = 6,
}) {
 // Split brands into pages
 const brandsPerPage = maxBrandsPerTable;
 const totalPages = Math.ceil(uniqueBrands.length / brandsPerPage);
  const metricExplainations = getMetricExplainations();
 const paginatedTables = Array.from({ length: totalPages }).map((_, pageIdx) => {
  const startIdx = pageIdx * brandsPerPage;
  const endIdx = startIdx + brandsPerPage;
  const pageBrands = uniqueBrands.slice(startIdx, endIdx);

  return {
   pageNumber: pageIdx + 1,
   brands: pageBrands,
   data: data,
  };
 });

 const renderRow = (mainItem, index) => {
  const allBrands = paginatedTables[0]?.brands || uniqueBrands;

  return (
   <StyledTableRow key={index}>
    <StyledTableCell className={styleClasses.attribute_headings}>
     {formatName(mainItem.name)}
    </StyledTableCell>

    {/* Loop through each brand in header order */}
    {allBrands.map((brandObj, brandIndex) => {
     // find matching score for this brand
     const scoreItem = mainItem.scores.find(
      (s) => s.name?.toLowerCase() === brandObj.name?.toLowerCase()
     );

     // fallback if not found
     if (!scoreItem || !scoreItem.score) {
      return (
       <StyledTableCell
        key={brandIndex}
        className={styleClasses.attribute_score}
        style={{ color: "grey", opacity: 0.6 }}
       >
        NA
       </StyledTableCell>
      );
     }

     // determine color
     let color = "#dc3545";
     if (scoreItem.score >= 7) color = "#2196F3";
     if (scoreItem.score >= 9) color = "#198754";

     return (
      <StyledTableCell
       key={brandIndex}
       style={{ color }}
       className={styleClasses.attribute_score}
      >
       {Number(scoreItem.score).toFixed(0)}
       &nbsp;&nbsp;
       {isChecked && scoreItem.delta !== null && (
        <span style={{ color: "grey" }}>
         <FontAwesomeIcon
          className={
           scoreItem.delta < 0
            ? `${styleClasses["core-down-icon"]} text-danger me-1`
            : `${styleClasses["core-up-icon"]} text-success me-1`
          }
          icon={scoreItem.delta < 0 ? faSortDown : faSortUp}
         />
         <span className={styleClasses.coreDelta}>
          {Number(Math.abs(scoreItem.delta)).toFixed(0)}
         </span>
        </span>
       )}
      </StyledTableCell>
     );
    })}
   </StyledTableRow>
  );
 };

 const renderHeadersForPage = (pageBrands, pageNumber) => {
  const TABLE_HEADERS = [
   {
    id: "attr",
    name: ENTITY_LABELS.purchaseCriteria,
   },
  ];

  const headers = [...TABLE_HEADERS, ...pageBrands];

  return (
   <TableHead className={styleClasses.table_head}>
    <TableRow>
     {headers.map((header, index) => {
      let domain = extractDomain(brandDomains?.[header?.name]);

      return (
       <TableCell key={index} className={styleClasses.table_head_cell}>
        <div className={styleClasses.table_head_content}>
         {index !== 0 && (
          <img
           data-html2canvas-ignore
           className={styleClasses.table_head_logo}
           src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
           alt={header.name}
           onError={(e) => {
            e.target.style.display = "none";
           }}
          />
         )}
         <div
          className={
           index === 0
            ? styleClasses.table_head_text_comp
            : styleClasses.table_head_text
          }
         >
          <div className={styleClasses.table_head_title}>
           <span>
            <Tooltip title={metricExplainations?.[header.name] || ""}>
             {header.name}
            </Tooltip>
           </span>
          </div>
         </div>
        </div>
       </TableCell>
      );
     })}
    </TableRow>
   </TableHead>
  );
 };

 const renderPageTable = (pageData) => {
  return (
   <div
    key={`page-${pageData.pageNumber}`}
    style={{
     pageBreakAfter:
      pageData.pageNumber < totalPages ? "always" : "auto",
     marginBottom: pageData.pageNumber < totalPages ? "30px" : "0",
     border: "1px solid #e0e0e0",
     padding: "16px",
     borderRadius: "4px",
     backgroundColor: "#fff",
    }}
   >
    <div
     style={{
      textAlign: "right",
      fontSize: "12px",
      color: "#666",
      marginBottom: "8px",
      fontWeight: "bold",
     }}
    >
     Page {pageData.pageNumber} of {totalPages}
    </div>

    <Table
     elevation={0}
     data={pageData.data}
     renderHeaders={() =>
      renderHeadersForPage(pageData.brands, pageData.pageNumber)
     }
     render={(item, idx) => {
      // Override the render to use filtered brands for this page
      const attName = formatName(item.name);
      return (
       <StyledTableRow key={idx}>
        <StyledTableCell className={styleClasses.attribute_headings}>
         {formatName(item.name)}
        </StyledTableCell>

        {pageData.brands.map((brandObj, brandIndex) => {
         const scoreItem = item.scores.find(
          (s) => s.name?.toLowerCase() === brandObj.name?.toLowerCase()
         );

         if (!scoreItem || !scoreItem.score) {
          return (
           <StyledTableCell
            key={brandIndex}
            className={styleClasses.attribute_score}
            style={{ color: "grey", opacity: 0.6 }}
           >
            NA
           </StyledTableCell>
          );
         }

         let color = "#dc3545";
         if (scoreItem.score >= 7) color = "#2196F3";
         if (scoreItem.score >= 9) color = "#198754";

         return (
          <StyledTableCell
           key={brandIndex}
           style={{ color }}
           className={styleClasses.attribute_score}
          >
           {Number(scoreItem.score).toFixed(0)}
           &nbsp;&nbsp;
           {isChecked && scoreItem.delta !== null && (
            <span style={{ color: "grey" }}>
             <FontAwesomeIcon
              className={
               scoreItem.delta < 0
                ? `${styleClasses["core-down-icon"]} text-danger me-1`
                : `${styleClasses["core-up-icon"]} text-success me-1`
              }
              icon={scoreItem.delta < 0 ? faSortDown : faSortUp}
             />
             <span className={styleClasses.coreDelta}>
              {Number(Math.abs(scoreItem.delta)).toFixed(0)}
             </span>
            </span>
           )}
          </StyledTableCell>
         );
        })}
       </StyledTableRow>
      );
     }}
    />
   </div>
  );
 };

 return (
  <div className="paginated-category-dimensions-tables">
   {paginatedTables.map((pageData) => renderPageTable(pageData))}
  </div>
 );
}

export { PaginatedCategoryDimensionsTable };